/**
 * Offer field helpers (frontend mirror).
 *
 * Source of truth lives at:
 *   `packages/shared/src/utils/offer-field.utils.ts`
 *
 * This file duplicates the runtime helpers (no Zod, no schema export) because
 * the frontend currently does not depend on `@pai/shared`. Keep the two in
 * sync. If/when the frontend gains `@pai/shared` as a workspace dependency,
 * delete this file and import from `@pai/shared` directly.
 *
 * Background: campaign offer fields (`jobTitle`, `jobOffer`, `problems`,
 * `features`, `benefits`, `impact`) can be either a plain `string` (legacy /
 * initial DB load) or `{ inUse: boolean, value: string }` (after the user has
 * toggled or edited the field in the campaign builder). The Mongo schema
 * stores both shapes via `Schema.Types.Mixed`. Always pass values through
 * {@link coerceOfferFieldToString} before sending them to any API that
 * expects a string (e.g. `/talent-search`), and use {@link getOfferFieldInUse}
 * when the toggle state matters.
 */

export type OfferFieldValue =
  | string
  | null
  | undefined
  | {
      inUse?: boolean;
      value?: string | null;
      [key: string]: unknown;
    };

function isOfferFieldObject(
  field: unknown,
): field is { inUse?: boolean; value?: unknown } {
  return (
    typeof field === 'object' &&
    field !== null &&
    !Array.isArray(field) &&
    ('value' in (field as Record<string, unknown>) ||
      'inUse' in (field as Record<string, unknown>))
  );
}

/**
 * Coerce any offer field shape into a plain string.
 *
 * Returns `''` for `null`, `undefined`, or unknown shapes. Does **not** respect
 * the `inUse` flag — use {@link offerFieldForPrompt} when an opted-out field
 * must be blanked out.
 */
export function coerceOfferFieldToString(field: OfferFieldValue): string {
  if (field == null) return '';
  if (typeof field === 'string') return field;
  if (isOfferFieldObject(field)) {
    const raw = field.value;
    if (raw == null) return '';
    return typeof raw === 'string' ? raw : String(raw);
  }
  return '';
}

/**
 * Read the `inUse` flag of an offer field. Defaults to `true` for plain
 * strings or shapes that do not carry an `inUse` flag.
 */
export function getOfferFieldInUse(field: OfferFieldValue): boolean {
  if (field == null) return true;
  if (typeof field === 'string') return true;
  if (isOfferFieldObject(field) && typeof field.inUse === 'boolean') {
    return field.inUse;
  }
  return true;
}

/**
 * Prompt-aware coercion: returns `''` when the user has explicitly toggled
 * the field off (`inUse === false`).
 */
export function offerFieldForPrompt(field: OfferFieldValue): string {
  if (!getOfferFieldInUse(field)) return '';
  return coerceOfferFieldToString(field);
}
